import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { CircleNotch, Sparkle, TrendUp, Flame, ChartLine, Plus } from "@phosphor-icons/react";
import AddAnalyticsModal from "@/components/AddAnalyticsModal";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";

const PALETTE = {
  pink: "#FFA5BC",
  accent: "var(--accent)",
  rose: "#C64AA0",
  ink: "#0a0a0a",
  soft: "var(--bg-soft)",
  line: "var(--line)",
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [advanced, setAdvanced] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState("");
  const [genning, setGenning] = useState(false);
  const [engagement, setEngagement] = useState({ posts_commented_on: 0, comments_received: 0 });
  const [savingEng, setSavingEng] = useState(false);
  const [showAddAnalytics, setShowAddAnalytics] = useState(false);

  const load = async () => {
    const [r, a] = await Promise.all([
      api.get("/analytics/summary"),
      api.get("/analytics/advanced"),
    ]);
    setData(r.data);
    setAdvanced(a.data);
    setEngagement({
      posts_commented_on: r.data.engagement?.posts_commented_on || 0,
      comments_received: r.data.engagement?.comments_received || 0,
    });
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveEngagement = async () => {
    setSavingEng(true);
    try { await api.put("/analytics/engagement", engagement); toast.success("This week's engagement saved."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEng(false); }
  };

  const generateRec = async () => {
    setGenning(true);
    try { const r = await api.post("/analytics/recommendations"); setRec(r.data.recommendations || ""); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setGenning(false); }
  };

  const t = data?.totals || {};
  const stats = [
    ["Published", t.posts_published || 0],
    ["Impressions", t.impressions || 0],
    ["Avg / post", t.avg_impressions || 0],
    ["Engagement %", `${t.engagement_rate || 0}%`],
    ["Save rate", `${t.save_rate || 0}%`],
    ["Comment rate", `${t.comment_rate || 0}%`],
  ];
  const pillarData = data ? Object.entries(data.by_pillar).map(([k, v]) => ({ pillar: k, impressions: v.impressions, engagement: v.engagement, saves: v.saves })) : [];

  const bestDay = (advanced?.day_of_week || []).filter(d => d.posts > 0).sort((a, b) => b.avg_impressions - a.avg_impressions)[0];
  const bestPillar = (advanced?.pillar_leaderboard || [])[0];

  return (
    <div>
      <PageHeader
        eyebrow="Analytics"
        title="What worked. What she'll learn."
        subtitle="Enter performance on each Published post in the Planner. Log your weekly outbound engagement below. She uses everything to weight your winning patterns."
        action={
          <button
            data-testid="add-analytics-btn"
            className="btn btn-accent"
            onClick={() => setShowAddAnalytics(true)}
          >
            <Plus size={16} weight="bold"/> Add analytics
          </button>
        }
      />
      <AddAnalyticsModal
        open={showAddAnalytics}
        onClose={() => setShowAddAnalytics(false)}
        onSaved={load}
      />

      {loading ? <div className="text-sm" style={{ color: 'var(--ink-3)' }}>Loading…</div> : (
        <>
          {/* Core stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            {stats.map(([label, val]) => (
              <div key={label} className="card p-5 fade-up" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, '')}`}>
                <div className="eyebrow mb-2">{label}</div>
                <div className="font-serif text-3xl tracking-tight font-semibold">{typeof val === "number" ? val.toLocaleString() : val}</div>
              </div>
            ))}
          </div>

          {/* Insight strip: streak, best day, best pillar */}
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <div className="card p-5 fade-up flex items-center gap-4" data-testid="insight-streak">
              <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--accent-tint)', color: 'var(--accent)' }}>
                <Flame size={20} weight="fill" />
              </div>
              <div>
                <div className="eyebrow mb-1">Posting streak</div>
                <div className="font-serif text-2xl font-semibold">{advanced?.streak_days || 0} <span className="text-sm" style={{ color: 'var(--ink-3)' }}>days</span></div>
                <div className="text-xs" style={{ color: 'var(--ink-3)' }}>{advanced?.consistency_14 || 0}/14 last two weeks</div>
              </div>
            </div>
            <div className="card p-5 fade-up flex items-center gap-4" data-testid="insight-best-day">
              <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--accent-tint)', color: 'var(--accent)' }}>
                <TrendUp size={20} weight="fill" />
              </div>
              <div>
                <div className="eyebrow mb-1">Best day so far</div>
                <div className="font-serif text-2xl font-semibold">{bestDay?.day || "—"}</div>
                <div className="text-xs" style={{ color: 'var(--ink-3)' }}>{bestDay ? `${bestDay.avg_impressions.toLocaleString()} avg impressions` : "Publish a few days to unlock"}</div>
              </div>
            </div>
            <div className="card p-5 fade-up flex items-center gap-4" data-testid="insight-best-pillar">
              <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--accent-tint)', color: 'var(--accent)' }}>
                <ChartLine size={20} weight="fill" />
              </div>
              <div>
                <div className="eyebrow mb-1">Top pillar by engagement %</div>
                <div className="font-serif text-2xl font-semibold truncate max-w-[220px]">{bestPillar?.pillar || "—"}</div>
                <div className="text-xs" style={{ color: 'var(--ink-3)' }}>{bestPillar ? `${bestPillar.engagement_rate}% eng · ${bestPillar.posts} posts` : "Assign pillars to posts"}</div>
              </div>
            </div>
          </div>

          {/* Weekly engagement input */}
          <div className="card p-6 mb-6" data-testid="weekly-engagement">
            <div className="eyebrow mb-3">This week's outbound engagement</div>
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <label className="block">
                <div className="text-xs mb-1" style={{ color: 'var(--ink-3)' }}>Posts I commented on</div>
                <input data-testid="eng-commented" type="number" className="input" value={engagement.posts_commented_on}
                  onChange={e => setEngagement({ ...engagement, posts_commented_on: parseInt(e.target.value || "0") })} />
              </label>
              <label className="block">
                <div className="text-xs mb-1" style={{ color: 'var(--ink-3)' }}>Comments received</div>
                <input data-testid="eng-received" type="number" className="input" value={engagement.comments_received}
                  onChange={e => setEngagement({ ...engagement, comments_received: parseInt(e.target.value || "0") })} />
              </label>
              <button data-testid="eng-save" onClick={saveEngagement} disabled={savingEng} className="btn btn-accent h-11">{savingEng ? "Saving…" : "Save this week"}</button>
            </div>
          </div>

          {/* AI recommendations */}
          <div className="card p-6 mb-6" data-testid="ai-recommendations" style={{ background: "var(--accent-tint)" }}>
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="eyebrow mb-1 flex items-center gap-2" style={{ color: 'var(--accent-2)' }}>
                  <Sparkle size={12} weight="fill" /> AI strategic recommendations
                </div>
                <h3 className="font-serif text-2xl font-semibold">What to focus on next.</h3>
              </div>
              <button data-testid="rec-generate" onClick={generateRec} disabled={genning || !t.posts_published} className="btn btn-accent">
                {genning ? <><CircleNotch size={14} className="animate-spin" /> Analyzing</> : (rec ? "Refresh" : "Generate")}
              </button>
            </div>
            {rec ? <Markdown>{rec}</Markdown> : (
              <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{t.posts_published ? "Click generate to get 3-5 strategic recommendations based on your actual numbers." : "Publish a few posts with metrics entered, then generate recommendations."}</p>
            )}
          </div>

          {/* Impressions & engagement over time */}
          <div className="card p-6 mb-6" data-testid="chart-timeseries">
            <div className="eyebrow mb-4">Growth trajectory (per publish date)</div>
            {(advanced?.timeseries || []).length < 2 ? (
              <div className="text-sm py-12 text-center" style={{ color: 'var(--ink-3)' }}>Publish 2+ posts with dates + impressions to see your trajectory.</div>
            ) : (
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={advanced.timeseries}>
                    <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-elev)', border: '1px solid var(--line-2)', borderRadius: 16, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="impressions" stroke={PALETTE.pink} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="engagement" stroke="var(--accent)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Best day + Length band + Hook length: 3 columns */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="card p-6" data-testid="chart-day-of-week">
              <div className="eyebrow mb-4">Avg impressions by day of week</div>
              {(advanced?.day_of_week || []).every(d => d.posts === 0) ? (
                <div className="text-sm py-10 text-center" style={{ color: 'var(--ink-3)' }}>Publish posts on different days to reveal your best day.</div>
              ) : (
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={advanced.day_of_week}>
                      <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                      <Tooltip contentStyle={{ background: 'var(--bg-elev)', border: '1px solid var(--line-2)', borderRadius: 16, fontSize: 12 }} />
                      <Bar dataKey="avg_impressions" fill={PALETTE.rose} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="card p-6" data-testid="chart-hook-band">
              <div className="eyebrow mb-4">Hook length → avg impressions</div>
              {(advanced?.hook_perf || []).every(h => h.posts === 0) ? (
                <div className="text-sm py-10 text-center" style={{ color: 'var(--ink-3)' }}>Not enough data yet. Publish posts with varied hook lengths.</div>
              ) : (
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={advanced.hook_perf}>
                      <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
                      <XAxis dataKey="band" tick={{ fontSize: 10, fill: 'var(--ink-2)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                      <Tooltip contentStyle={{ background: 'var(--bg-elev)', border: '1px solid var(--line-2)', borderRadius: 16, fontSize: 12 }} />
                      <Bar dataKey="avg_impressions" fill={PALETTE.pink} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Engagement funnel + length bands */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="card p-6" data-testid="chart-funnel">
              <div className="eyebrow mb-4">Engagement funnel</div>
              {(advanced?.funnel?.[0]?.value || 0) === 0 ? (
                <div className="text-sm py-10 text-center" style={{ color: 'var(--ink-3)' }}>Enter impressions, reactions, comments, and saves in the Planner.</div>
              ) : (
                <div className="space-y-3">
                  {advanced.funnel.map((row) => (
                    <div key={row.stage}>
                      <div className="flex items-baseline justify-between text-xs mb-1">
                        <span className="eyebrow">{row.stage}</span>
                        <span className="font-mono" style={{ color: 'var(--ink-3)' }}>{row.value.toLocaleString()} · {row.pct}%</span>
                      </div>
                      <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--line)' }}>
                        <div style={{ width: `${Math.min(100, row.pct)}%`, background: 'var(--grad-warm)', height: '100%', transition: 'width 500ms ease' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card p-6" data-testid="chart-length-band">
              <div className="eyebrow mb-4">Post length → engagement rate</div>
              {(advanced?.length_perf || []).every(l => l.posts === 0) ? (
                <div className="text-sm py-10 text-center" style={{ color: 'var(--ink-3)' }}>Publish short, medium, and long posts to compare.</div>
              ) : (
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={advanced.length_perf}>
                      <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
                      <XAxis dataKey="band" tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--ink-2)' }} />
                      <Tooltip contentStyle={{ background: 'var(--bg-elev)', border: '1px solid var(--line-2)', borderRadius: 16, fontSize: 12 }} formatter={(v) => `${v}%`} />
                      <Bar dataKey="engagement_rate" fill="var(--accent)" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Performance by pillar (bar) */}
          <div className="card p-6 mb-6">
            <div className="eyebrow mb-4">Performance by pillar</div>
            {pillarData.length === 0 ? (
              <div className="text-sm py-12 text-center" style={{ color: 'var(--ink-3)' }}>Publish some posts and enter metrics in the Planner to see this come alive.</div>
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={pillarData}>
                    <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
                    <XAxis dataKey="pillar" tick={{ fontSize: 12, fill: 'var(--ink-2)' }} />
                    <YAxis tick={{ fontSize: 12, fill: 'var(--ink-2)' }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-elev)', border: '1px solid var(--line-2)', borderRadius: 16, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="impressions" fill={PALETTE.pink} radius={[8, 8, 0, 0]} />
                    <Bar dataKey="engagement" fill="var(--accent)" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="saves" fill={PALETTE.rose} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Pillar leaderboard */}
          {(advanced?.pillar_leaderboard || []).length > 0 && (
            <div className="card p-6 mb-6" data-testid="pillar-leaderboard">
              <div className="eyebrow mb-4">Pillar leaderboard, engagement rate</div>
              <div className="space-y-2">
                {advanced.pillar_leaderboard.map((p, i) => (
                  <div key={p.pillar} className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: 'var(--bg-soft)' }}>
                    <span className="font-serif text-2xl w-8 font-semibold" style={{ color: 'var(--accent)' }}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{p.pillar}</div>
                      <div className="text-xs font-mono" style={{ color: 'var(--ink-3)' }}>
                        {p.posts} posts · {p.impressions.toLocaleString()} impr · {p.avg_impressions.toLocaleString()} avg
                      </div>
                    </div>
                    <span className="chip chip-accent">{p.engagement_rate}% eng</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top posts */}
          <div className="card p-6">
            <div className="eyebrow mb-4">Top posts</div>
            {(data?.top_posts || []).length === 0 ? (
              <div className="text-sm py-8 text-center" style={{ color: 'var(--ink-3)' }}>Once you publish posts and enter impressions, your top 5 will surface here.</div>
            ) : (
              <ol className="space-y-3">
                {data.top_posts.map((p, i) => (
                  <li key={p.id} className="p-4 rounded-2xl flex gap-4 items-start" style={{ background: "var(--bg-soft)" }}>
                    <span className="font-serif text-3xl w-10 font-semibold" style={{ color: 'var(--accent)' }}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm line-clamp-2">{p.content || p.topic}</div>
                      <div className="text-xs mt-1 font-mono" style={{ color: 'var(--ink-3)' }}>
                        {p.impressions.toLocaleString()} impr · {p.reactions} reactions · {p.comments} comments · {p.saves} saves
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}
